import { useFormContext } from 'react-hook-form';
import { PaymentMethodCard } from 'src/components/PaymentMethod';
import { useAppContext } from 'src/contexts/AppContext';
import { useContent } from 'src/contexts/ContentContext';
import { Radio, RadioGroup, Stack } from 'src/core/ui';
import { useGetBuyerCredit } from 'src/hooks/api/useGetBuyerCredit';
import { useGetBuyerPaymentMethods } from 'src/hooks/api/useGetBuyerPaymentMethods';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { DetailSection } from 'src/modals/common/DetailSection';
import { useRetailSaleContext } from 'src/modals/RetailSale/RetailSaleLocalContext';
import { ContentId } from 'src/utils/constants/contentId';
import { formatCurrency } from 'src/utils/numberFormatter';
import { ExternalPaymentMethodType, Feature } from 'src/WebApiController';

import { PaymentMethodChoice, RetailSaleFormValues } from '../types';
import { ExternalPaymentSection } from './ExternalPaymentSection';
import { PartialPaymentInput } from './PartialPaymentInput';
import * as styles from './RetailSaleCheckoutStep.css';

// Sentinel RadioGroup value for the "Add new card" option; maps to a null
// selectedStoredPaymentMethodId (charge a brand-new card rather than a saved one).
const ADD_NEW_CARD_VALUE = 'add-new-card';

/**
 * Shared PAYMENT METHOD section for the retail-sale checkout. Rendered
 * unchanged by both the single-listing (`RetailSaleCheckoutStep`) and the
 * multi-listing (`MultiListingRetailSaleCheckoutStep`) flows so the payment
 * chrome — mode radio, partial payment, saved cards, external payment form,
 * and the partial coverage dialog — stays in lockstep across the two entry
 * points.
 */
export const RetailSalePaymentSection = () => {
  const {
    disabled,
    selectedBuyer,
    selectedStoredPaymentMethodId,
    setSelectedStoredPaymentMethodId,
    isPartialPayment,
    setIsPartialPayment,
    partialAmount,
    setPartialAmount,
  } = useRetailSaleContext();

  const { loginContext } = useAppContext();
  const { watch, setValue } = useFormContext<RetailSaleFormValues>();

  const totalNetProceeds = watch('totalNetProceeds');
  const marketplacePaymentLines = watch('marketplacePaymentLines');
  const paymentMethodChoice = watch('paymentMethodChoice') ?? 'adyen';
  const currencyCode =
    watch('currencyCode') ??
    loginContext?.user?.activeAccount.currencyCode ??
    'USD';

  const totalAmount =
    (totalNetProceeds ?? 0) +
    (marketplacePaymentLines ?? []).reduce(
      (sum, l) => sum + (l.amount ?? 0),
      0
    );

  const paymentMsg = useContent(ContentId.Payment);
  const addNewCardMsg = useContent(ContentId.AddNewCard);
  const creditPaymentNote = useContent(ContentId.PaidViaCustomerCredit);
  const customerRemainingCreditLabel = useContent(
    ContentId.CustomerRemainingCredit
  );

  const { data: savedPaymentMethods = [] } = useGetBuyerPaymentMethods(
    selectedBuyer?.buyerEmail
  );

  // Gate the "Paid Externally" option behind the RetailSale feature flag.
  const hasRetailSaleFeature = useUserHasFeature(Feature.RetailSale);

  // POS-8726: Fetch buyer credit balance for "Pay by Customer Credit" option
  const { data: buyerCredit } = useGetBuyerCredit(
    selectedBuyer?.buyerEmail,
    !hasRetailSaleFeature,
    currencyCode // Convert credit to the sale's currency for display
  );
  const availableCreditBalance = buyerCredit?.balance ?? 0;

  const isExternalPayment = paymentMethodChoice === 'external';

  const handlePaymentChoiceChange = (value: string) => {
    const choice = value as PaymentMethodChoice;
    setValue('paymentMethodChoice', choice);

    if (choice === 'external') {
      const today = new Date().toISOString().split('T')[0];
      setValue('externalPaymentDate', today);
      setValue('externalPaymentAmount', totalAmount > 0 ? totalAmount : null);
    } else if (choice === 'credit') {
      // POS-8726: Set credit payment fields. Don't set externalPaymentAmount here —
      // it's derived from the current total at submission time so it stays in sync
      // when the user changes quantity/listings after selecting credit.
      setValue(
        'externalPaymentMethodType',
        ExternalPaymentMethodType.CustomerCredit
      );
      setValue('externalPaymentNote', creditPaymentNote);
      setValue('externalPaymentDate', new Date().toISOString().split('T')[0]);
    } else {
      setValue('externalPaymentMethodType', null);
      setValue('externalPaymentNote', null);
      setValue('externalPaymentAmount', null);
      setValue('externalPaymentDate', null);
    }
  };

  return (
    <>
      <DetailSection name={paymentMsg}>
        <Stack direction="column" gap="m">
          <RadioGroup
            value={paymentMethodChoice}
            onValueChange={handlePaymentChoiceChange}
            disabled={disabled}
          >
            <Stack width="full" direction="column" gap="m">
              <Radio
                alignItems="start"
                variant="outline"
                value="adyen"
                label="Paid in POS"
                containerClassName={styles.paymentItem}
                labelSubtext={
                  paymentMethodChoice === 'adyen' && (
                    <Stack width="full" direction="column" gap="m">
                      <RadioGroup
                        value={
                          selectedStoredPaymentMethodId ?? ADD_NEW_CARD_VALUE
                        }
                        onValueChange={(value) =>
                          setSelectedStoredPaymentMethodId(
                            value === ADD_NEW_CARD_VALUE ? null : value
                          )
                        }
                        disabled={disabled}
                      >
                        {savedPaymentMethods.map((card) => (
                          <Radio
                            key={card.storedPaymentMethodId}
                            value={card.storedPaymentMethodId}
                            variant="outline"
                            disabled={disabled}
                            label={
                              <PaymentMethodCard
                                flattened
                                cardBrand={card.cardBrand}
                                cardSummary={card.cardSummary}
                                expiryMonth={card.expiryMonth}
                                expiryYear={card.expiryYear}
                                isDefault={card.isDefault}
                              />
                            }
                          />
                        ))}

                        <Radio
                          value={ADD_NEW_CARD_VALUE}
                          variant="outline"
                          disabled={disabled}
                          label={addNewCardMsg}
                        />
                      </RadioGroup>

                      {/* Partial payment toggle — visible for all payment flows */}
                      <PartialPaymentInput
                        disabled={disabled}
                        totalAmount={totalAmount}
                        currencyCode={currencyCode}
                        isPartialPayment={isPartialPayment}
                        partialAmount={partialAmount}
                        onIsPartialPaymentChange={setIsPartialPayment}
                        onPartialAmountChange={setPartialAmount}
                      />
                    </Stack>
                  )
                }
                disabled={disabled}
              />
              <Radio
                variant="outline"
                value="external"
                label="Paid Externally (Cash / Wire / Other)"
                alignItems="start"
                containerClassName={styles.paymentItem}
                labelSubtext={
                  isExternalPayment && (
                    <ExternalPaymentSection
                      disabled={disabled}
                      totalAmount={totalAmount}
                    />
                  )
                }
                disabled={disabled}
              />
              <Radio
                alignItems="start"
                variant="outline"
                value="credit"
                label="Pay by Customer Credit"
                containerClassName={styles.paymentItem}
                labelSubtext={
                  <>
                    <span className={styles.creditBalanceHint}>
                      {`${customerRemainingCreditLabel}: ${formatCurrency(availableCreditBalance, currencyCode)}`}
                    </span>
                    {paymentMethodChoice === 'credit' && (
                      <PartialPaymentInput
                        disabled={disabled}
                        totalAmount={totalAmount}
                        currencyCode={currencyCode}
                        isPartialPayment={isPartialPayment}
                        partialAmount={partialAmount}
                        onIsPartialPaymentChange={setIsPartialPayment}
                        onPartialAmountChange={setPartialAmount}
                      />
                    )}
                  </>
                }
                disabled={disabled}
              />
              <Radio
                variant="outline"
                value="unpaid"
                label="Unpaid"
                containerClassName={styles.paymentItem}
                disabled={disabled}
              />
            </Stack>
          </RadioGroup>
        </Stack>
      </DetailSection>
    </>
  );
};
